import { createCapaActionSchema, type CreateCapaActionInput } from '@qualicloud/contracts';
import { useMemo, useState } from 'react';
import {
  Select,
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  Dialog,
  Field,
  FormError,
  Input,
  NumberInput,
  Textarea,
  toIso,
} from '@/design-system';
import { formLevelMessage, zodIssuesToFieldErrors } from '@/lib/sgq/form-errors';
import type { RequestType } from '@/lib/api';
import { CadastroRapidoDeTipoDeSolicitacao } from '@/components/cadastro-rapido';
import type { PeopleOption } from './people-picker';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLANO DE AÇÃO 5W2H — os sete campos, ESTRUTURADOS
 * sgq.md §C.1
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O DEFEITO QUE ESTA TELA CORRIGE (BUG-TAR-01)
 * Os sete campos `w_what`..`w_howmuch` existiam na tabela do legado e eram
 * INALCANÇÁVEIS pela interface. Só `reuniao/tarefas/addTask` os gravava — num
 * `UPDATE` separado, dentro de um `try/catch` que apenas logava — e NENHUM
 * template do sistema chamava aquela rota. Toda tarefa real nascia por
 * `reuniao/reuniao/addSS`, cujo INSERT não contemplava o 5W2H. Resultado: as
 * colunas ficavam sempre NULL e a seção "Plano de ação 5W2H" do PDF da NC saía
 * vazia. O requisito estava implementado no backend e ausente do produto.
 *
 * DOIS CAMPOS DEIXARAM DE SER TEXTO LIVRE
 *  · When era `VARCHAR(50)` com a data em `dd/mm/aaaa`, o que tornava o prazo
 *    incomparável: nenhuma consulta conseguia dizer "vencidas". Aqui é `DATE`, e
 *    é o que alimenta `resolveCapaDueState`;
 *  · Who era `VARCHAR(255)` com o nome digitado. Aqui é o `userId` do
 *    responsável, o que permite atribuir, reatribuir e cobrar.
 *
 * `howMuch` continua OPCIONAL por decisão de domínio (`@qualicloud/domain/capa`):
 * nem toda ação corretiva tem custo apurável, e exigir um número inventado
 * degrada o dado.
 */

export interface CapaActionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Causa-raiz que a solicitação ataca, quando ela nasce do tratamento de uma
   * não-conformidade. `null` abre o diálogo em modo AVULSO.
   *
   * O mesmo diálogo serve aos dois casos de propósito: são o mesmo formulário
   * com a mesma validação, e dois diálogos separados divergiriam na primeira
   * correção feita em só um deles.
   */
  readonly rootCauseId: string | null;
  readonly rootCauseDescription: string | null;
  /** Catálogo de tipos ATIVOS. O tipo escolhido decide o que será exigido. */
  readonly requestTypes: readonly RequestType[];
  readonly defaultDepartmentId?: string | undefined;
  readonly users: readonly PeopleOption[];
  readonly departments: readonly PeopleOption[];
  readonly submitting?: boolean;
  readonly submitError?: string | null;
  readonly onSubmit: (input: CreateCapaActionInput) => void | Promise<void>;
}

interface FormState {
  readonly requestTypeId: string;
  readonly title: string;
  readonly what: string;
  readonly why: string;
  readonly where: string;
  readonly dueDate: string;
  readonly assigneeUserId: string;
  readonly how: string;
  readonly howMuch: string;
  readonly departmentId: string;
  readonly preventive: boolean;
}

function emptyState(departmentId: string | undefined, requestTypeId: string): FormState {
  return {
    requestTypeId,
    title: '',
    what: '',
    why: '',
    where: '',
    dueDate: toIso(new Date()),
    assigneeUserId: '',
    how: '',
    howMuch: '',
    departmentId: departmentId ?? '',
    preventive: false,
  };
}

export function CapaActionDialog({
  open,
  onOpenChange,
  rootCauseId,
  rootCauseDescription,
  requestTypes,
  defaultDepartmentId,
  users,
  departments,
  submitting = false,
  submitError,
  onSubmit,
}: CapaActionDialogProps) {
  /*
   * Tipos oferecidos: no modo AVULSO, só os que dispensam causa-raiz.
   *
   * Deixar "Ação corretiva" na lista de um formulário sem causa faria a pessoa
   * preencher os oito campos para levar 422 no fim. Filtrar aqui é mais honesto
   * do que validar depois — e o servidor continua recusando, porque a lista da
   * tela nunca é a garantia.
   */
  const tiposOferecidos = useMemo(
    () => requestTypes.filter((type) => (rootCauseId ? true : !type.requiresRootCause)),
    [requestTypes, rootCauseId],
  );

  const [form, setForm] = useState<FormState>(() =>
    emptyState(defaultDepartmentId, tiposOferecidos[0]?.id ?? ''),
  );
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});

  /*
   * NÃO existe efeito de "limpar ao abrir" aqui, e a ausência é deliberada.
   *
   * Havia um, com `tiposOferecidos` nas dependências — um `useMemo` que devolve
   * array novo quando a identidade de `requestTypes` muda. Cadastrar um tipo
   * pelo atalho do próprio diálogo invalida a consulta de tipos; o React Query
   * espera o refetch antes de devolver o id novo, a identidade do array troca,
   * o efeito dispara e `setForm(emptyState(...))` apagava os oito campos já
   * preenchidos do 5W2H. Em silêncio, sem desfazer, exatamente no fluxo que o
   * cadastro rápido existe para proteger.
   *
   * O formulário já nasce limpo sem efeito nenhum: os dois chamadores montam o
   * diálogo sob condição (`capa-actions.page.tsx`, `meeting-detail.page.tsx`),
   * então o inicializador preguiçoso do `useState` roda a cada abertura. Quem
   * for acrescentar um reset aqui: confira antes se o diálogo continua montado
   * sob condição — se continuar, o reset é desnecessário e este defeito volta.
   */

  const tipoEscolhido = useMemo(
    () => tiposOferecidos.find((type) => type.id === form.requestTypeId) ?? null,
    [tiposOferecidos, form.requestTypeId],
  );
  /** Só o tipo decide se "por quê" e "como" são cobrados. */
  const exigePlanoCompleto = tipoEscolhido?.requiresFullPlan ?? true;

  const patch = (next: Partial<FormState>) => setForm((current) => ({ ...current, ...next }));

  /**
   * O comando é montado e validado pelo schema DO CONTRATO — o mesmo que o DTO
   * NestJS usa. `howMuch` vazio vira `null`, nunca `0`: string vazia num campo
   * numérico significa "não informado", e `z.coerce.number()` transformaria
   * `''` em `0`, declarando um custo zero que ninguém digitou.
   */
  const handleSubmit = () => {
    const candidate = {
      title: form.title,
      requestTypeId: form.requestTypeId,
      rootCauseId,
      what: form.what,
      why: form.why,
      where: form.where,
      dueDate: form.dueDate,
      assigneeUserId: form.assigneeUserId,
      how: form.how,
      howMuch: form.howMuch.trim() === '' ? null : form.howMuch,
      departmentId: form.departmentId,
      preventive: form.preventive,
    };

    const parsed = createCapaActionSchema.safeParse(candidate);
    if (!parsed.success) {
      setFieldErrors(zodIssuesToFieldErrors(parsed.error));
      return;
    }

    /*
     * O contrato aceita "por quê" e "como" vazios — quem os exige é o TIPO, e o
     * contrato não conhece o catálogo. A cobrança acontece aqui e no servidor:
     * aqui para a pessoa ver o campo marcado antes de enviar, e lá porque a
     * validação da tela nunca é garantia de nada.
     */
    if (exigePlanoCompleto) {
      const faltando: Record<string, string> = {};
      if (form.why.trim() === '') faltando.why = 'Informe por que a solicitação será executada.';
      if (form.how.trim() === '') faltando.how = 'Informe como a solicitação será executada.';
      if (Object.keys(faltando).length > 0) {
        setFieldErrors(faltando);
        return;
      }
    }

    setFieldErrors({});
    void onSubmit(parsed.data);
  };

  const formMessage = formLevelMessage(fieldErrors);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submitting) return;
        onOpenChange(next);
      }}
      dismissible={!submitting}
      size="lg"
      // "Solicitação", que é como o domínio sempre chamou (`erp_solicitacao`):
      // o mesmo formulário cria a corretiva, a preventiva e o pedido avulso.
      title={rootCauseId ? 'Nova solicitação a partir da causa' : 'Nova solicitação'}
      description={
        exigePlanoCompleto
          ? 'O plano 5W2H é cobrado por inteiro neste tipo, exceto o custo — ISO 13485 §8.5.2 exige a ação definida antes de ser executada.'
          : 'Este tipo dispensa "por quê" e "como": é pedido de trabalho, não plano de ação corretiva.'
      }
      footer={
        <>
          <Button variant="secondary" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            Criar solicitação
          </Button>
        </>
      }
    >
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Field
          label="Tipo da solicitação"
          required
          error={fieldErrors.requestTypeId}
          description={
            tipoEscolhido?.description ||
            'O tipo define o que será exigido desta solicitação.'
          }
        >
          <Select
            value={form.requestTypeId}
            onValueChange={(value) => patch({ requestTypeId: value })}
            placeholder="Escolha o tipo"
            criar={{
              rotulo: 'Novo tipo de solicitação',
              dialogo: (props) => <CadastroRapidoDeTipoDeSolicitacao {...props} />,
            }}
            options={tiposOferecidos.map((type) => ({ value: type.id, label: type.name }))}
          />
        </Field>

        {rootCauseId ? (
          <section
            aria-labelledby="capa-root-cause-heading"
            className="rounded-md border border-border bg-surface-sunken p-3"
          >
            <h3
              id="capa-root-cause-heading"
              className="text-2xs font-semibold uppercase tracking-wide text-content-subtle"
            >
              Causa-raiz atacada por esta solicitação
            </h3>
            <p className="mt-1 text-sm text-content">{rootCauseDescription}</p>
          </section>
        ) : (
          /*
           * O aviso é curto e factual. Uma solicitação avulsa não entra no plano
           * de ação de nenhuma não-conformidade, e quem a abre precisa saber
           * disso ANTES — descobrir depois, ao ver que a NC não fechou, seria
           * caro.
           */
          <p className="rounded-md border border-border bg-surface-sunken px-3 py-2.5 text-sm text-content-muted">
            Solicitação avulsa: não está ligada a nenhuma não-conformidade e não conta no
            plano de ação de nenhuma. Para tratar uma não-conformidade, abra a solicitação
            pela causa-raiz no Ishikawa dela.
          </p>
        )}

        {submitError ? (
          <FormError title="Não foi possível criar a ação">{submitError}</FormError>
        ) : null}
        {formMessage ? <FormError title="Revise o formulário">{formMessage}</FormError> : null}

        <Field label="Título da ação" required error={fieldErrors.title}>
          <Input
            value={form.title}
            onChange={(event) => patch({ title: event.target.value })}
            maxLength={255}
            autoFocus
          />
        </Field>

        <Field
          label="O quê — o que será feito"
          required
          error={fieldErrors.what}
          description="Descreva a ação concreta, não o resultado esperado."
        >
          <Textarea
            value={form.what}
            onChange={(event) => patch({ what: event.target.value })}
            rows={3}
          />
        </Field>

        <Field
          label="Por quê — por que a solicitação será executada"
          required={exigePlanoCompleto}
          error={fieldErrors.why}
          description="Liga a ação à causa-raiz acima. É o que distingue ação corretiva de correção pontual."
        >
          <Textarea
            value={form.why}
            onChange={(event) => patch({ why: event.target.value })}
            rows={3}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Onde — local ou processo" required error={fieldErrors.where}>
            <Input
              value={form.where}
              onChange={(event) => patch({ where: event.target.value })}
              maxLength={255}
            />
          </Field>

          <Field
            label="Quando — prazo"
            required
            error={fieldErrors.dueDate}
            description="Data limite para a conclusão. Alimenta o semáforo de vencimento."
          >
            <DatePicker
              value={form.dueDate}
              onValueChange={(iso) => patch({ dueDate: iso })}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Quem — responsável pela execução"
            required
            error={fieldErrors.assigneeUserId}
            description="Só o responsável registra início e conclusão da ação."
          >
            <Combobox
              items={users.map((user) => ({
                value: user.id,
                label: user.name,
                hint: user.hint,
              }))}
              value={form.assigneeUserId || null}
              onValueChange={(value) => patch({ assigneeUserId: value ?? '' })}
              placeholder="Buscar colaborador…"
              emptyMessage="Nenhum colaborador disponível."
            />
          </Field>

          <Field label="Departamento" required error={fieldErrors.departmentId}>
            <Combobox
              items={departments.map((department) => ({
                value: department.id,
                label: department.name,
              }))}
              value={form.departmentId || null}
              onValueChange={(value) => patch({ departmentId: value ?? '' })}
              placeholder="Buscar departamento…"
              emptyMessage="Nenhum departamento disponível."
            />
          </Field>
        </div>

        <Field
          label="Como — método de execução"
          required={exigePlanoCompleto}
          error={fieldErrors.how}
        >
          <Textarea
            value={form.how}
            onChange={(event) => patch({ how: event.target.value })}
            rows={3}
          />
        </Field>

        <Field
          label="Quanto custa"
          error={fieldErrors.howMuch}
          description="Opcional. Nem toda ação tem custo apurável, e um número inventado degrada o indicador."
        >
          <NumberInput
            value={form.howMuch}
            onChange={(event) => patch({ howMuch: event.target.value })}
            min={0}
            step="0.01"
            className="w-48"
          />
        </Field>

        <Checkbox
          checked={form.preventive}
          onCheckedChange={(checked) => patch({ preventive: checked })}
          label="Ação preventiva"
          description="Marque quando a ação evita a ocorrência de um problema potencial (ISO 13485 §8.5.3) em vez de tratar uma não-conformidade já ocorrida (§8.5.2)."
        />

        <button type="submit" className="qc-sr-only" tabIndex={-1} aria-hidden="true">
          Criar ação
        </button>
      </form>
    </Dialog>
  );
}
